<script setup lang="ts">
import { inject, onMounted, ref, watch } from "vue";

import ComfortIcon from "@/assets/icons/programs/comfort.svg?component";
import EcoIcon from "@/assets/icons/programs/eco.svg?component";
import FrostIcon from "@/assets/icons/programs/frost.svg?component";
import OnIcon from "@/assets/icons/programs/on.svg?component";
import TempIcon from "@/assets/icons/programs/temperature.svg?component";
import { IPilotingWizardState, PilotingWizardStateInjectKey } from "@/components/piloting/wizard/piloting-wizard.types";
import {
  EProgrammingGanttViewMode,
  IProgrammingGanttData,
  IProgrammingGanttRow,
  ProgrammingGantt,
  ProgrammingGanttDefaultOptions,
  TProgrammingGanttBarContent,
} from "@/components/programming-gantt/programming-gantt";
import {
  ISelectedSchedule,
  IProgrammingDeviceRow,
  IProgrammingDeviceSchedule,
} from "@/components/programming-gantt/programming-gantt.type";
import { useApplyPlanningOverridesBySection } from "@/composables/kili/internal/mutations/use-apply-planning-overrides-by-section";
import { useToaster } from "@/composables/layout/use-toaster";
import { TSectionPlanning } from "@/data/api/kili/sites/types/planning.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import {
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  ECommonProgramMetaMode,
  TProgramMetaMode,
  EBookableSpaceMetaMode,
  EProgrammingMetaMode,
} from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import $date from "@/utils/date";

interface IProps {
  siteId: number;
  plannings: Array<TSectionPlanning>;
  selectedApplianceCategory: EProgramApplianceCategory;
}

interface IEmits {
  (e: "refresh-plannings"): void;
  (e: "edit-planning", event: ISelectedSchedule): void;
}

const ProgrammingModeColorMap: { [K in TProgramMetaMode | EProgrammingMetaMode.PROGRAMMING]: string } = {
  [EHeaterProgramMetaMode.NO_FROST]: "no-frost",
  [EProgrammingMetaMode.PROGRAMMING]: "programming",
  [ECommonProgramMetaMode.OFF]: "stop",
  [EHeaterProgramMetaMode.ECO]: "eco",
  [EHeaterProgramMetaMode.COMFORT]: "comfort",
  [ECommonProgramMetaMode.TEMP]: "temperature",
  [EOnProgramMetaMode.ON]: "on",
  [EBookableSpaceMetaMode.AVAILABLE]: "on", // todo: change
  [EBookableSpaceMetaMode.OCCUPIED]: "stop", // todo: change
};

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const toast = useToaster();
const { t, k, d } = useTypedIntl();
const mounted = ref(false);

const { mutateAsync: applyPlanningOverridesBySection } = useApplyPlanningOverridesBySection();

const state = inject<IPilotingWizardState>(PilotingWizardStateInjectKey)!;

const data = defineModel<IProgrammingGanttData<IProgrammingDeviceRow, IProgrammingDeviceSchedule>>("modelValue", {
  default: {
    rows: [],
    schedules: [],
  },
  required: false,
});

const selectedRows = defineModel<Array<IProgrammingGanttRow>>("selection", {
  required: false,
  default: [],
});

const gantt = ref<ProgrammingGantt<(typeof data.value.rows)[number], (typeof data.value.schedules)[number]>>();

onMounted(() => {
  mounted.value = true;
});

watch(
  [data, mounted],
  () => {
    if (!mounted.value || !data.value.rows.length) {
      return;
    }

    let maybePreviousGanttCameraYPosition: number | undefined = undefined;

    if (gantt.value) {
      const { y } = gantt.value.getVisibleViewport();
      maybePreviousGanttCameraYPosition = y;
      gantt.value?.destroy();
    }

    gantt.value = new ProgrammingGantt("#gantt_here", data.value, {
      cameraYPosition: maybePreviousGanttCameraYPosition,
      viewMode: state.selectedViewMode ?? ProgrammingGanttDefaultOptions.viewMode,
      formatHeaderMonth(date: Date): string {
        return d(date, { month: "long", year: "numeric" });
      },
      formatHeaderDate(date) {
        if (date.getMonth() === 0 && date.getDate() === 1) {
          return d(date, { day: "2-digit", month: "short", year: "2-digit" });
        }

        return d(date, { day: "2-digit", month: "short" });
      },
      formatQuarter(date: Date) {
        if (date.getMonth() < 3) {
          return `Q1 ${date.getFullYear()}`;
        } else if (date.getMonth() < 6) {
          return `Q2 ${date.getFullYear()}`;
        } else if (date.getMonth() < 9) {
          return `Q3 ${date.getFullYear()}`;
        }

        return `Q4 ${date.getFullYear()}`;
      },
      getBarBackground(schedule) {
        return ProgrammingModeColorMap[schedule.type];
      },
      getBarContent(schedule): TProgrammingGanttBarContent {
        switch (schedule.type) {
          case EProgrammingMetaMode.PROGRAMMING:
            return {
              type: "text",
              label: t(k.global.program.metaMode[EProgrammingMetaMode.PROGRAMMING]),
            };
          case ECommonProgramMetaMode.OFF:
            return {
              type: "icon",
              icon: "pi-times-circle",
            };
          case EHeaterProgramMetaMode.ECO:
            return {
              type: "icon",
              icon: EcoIcon,
            };
          case EHeaterProgramMetaMode.NO_FROST:
            return {
              type: "icon",
              icon: FrostIcon,
            };
          case EHeaterProgramMetaMode.COMFORT:
            return {
              type: "icon",
              icon: ComfortIcon,
            };
          case ECommonProgramMetaMode.TEMP:
            if (schedule.temperature) {
              return {
                type: "text",
                label: `${schedule.temperature} ℃`,
              };
            }

            return {
              type: "icon",
              icon: TempIcon,
            };
          case EOnProgramMetaMode.ON:
            return {
              type: "icon",
              icon: OnIcon,
            };
          case EBookableSpaceMetaMode.AVAILABLE:
            return {
              type: "icon",
              icon: OnIcon,
            };
          case EBookableSpaceMetaMode.OCCUPIED:
            return {
              type: "icon",
              icon: "pi-times-circle",
            };
        }
      },
      onRowSelectionChanged(rows) {
        selectedRows.value = rows;
      },
      onContextMenuActionClick({ row, schedule: selectedSchedule, action }) {
        const planning = props.plannings.find((k) => `${k.sectionId}` === row.id);
        if (!planning) {
          return;
        }

        const schedulesWithoutSelectedSchedule = (planning?.ranges ?? []).filter((scheduleItem) => {
          const isSameEnd = scheduleItem.endTime === selectedSchedule.realEnd;
          const isSameStart = scheduleItem.startTime === selectedSchedule.realStart;
          const isSameMode = scheduleItem.metaMode === selectedSchedule.type;
          const isSame = isSameStart && isSameEnd && isSameMode;

          return !isSame;
        });

        if (action === "DELETE") {
          applyPlanningOverridesBySection({
            siteId: props.siteId,
            applianceCategory: props.selectedApplianceCategory,
            sectionRanges: [
              {
                sectionId: planning.sectionId,
                ranges: schedulesWithoutSelectedSchedule.map((k) => {
                  return {
                    ...k,
                    startTime: $date.formatDateWithoutTimezone(k.startTime),
                    endTime: $date.formatDateWithoutTimezone(k.endTime),
                  };
                }),
              },
            ],
          })
            .then(() => {
              emits("refresh-plannings");

              toast.add({
                severity: "success",
                detail: t(k.components.programmingGantt.messages.deleteSchedule.success),
              });

              MixpanelService.trackEvent(EMixpanelEvent.MANUAL_CONTROL_DELETED, {
                mode: selectedSchedule.type,
                duration: $date.diff(selectedSchedule.realEnd, selectedSchedule.realStart),
                temperature: selectedSchedule.temperature,
              });
            })
            .catch(() => {
              toast.add({
                severity: "error",
                detail: t(k.components.programmingGantt.messages.deleteSchedule.error),
              });
            });
        } else if (action === "EDIT") {
          const planning = props.plannings.find((k) => `${k.sectionId}` === row.id);

          if (!planning) {
            return;
          }

          emits("edit-planning", {
            initialSchedule: selectedSchedule,
            planning: {
              ...planning,
              ranges: schedulesWithoutSelectedSchedule,
            },
          });
        }
      },
      onChangeViewMode(newMode: EProgrammingGanttViewMode) {
        state.selectedViewMode = newMode;
      },
    });

    for (const row of selectedRows.value) {
      gantt.value.selectRow(row, false);
    }
  },
  { immediate: true },
);

function selectAll() {
  gantt.value?.selectAll();
}

function unselectAll() {
  gantt.value?.unselectAll();
}

function filter(cb: (row: IProgrammingDeviceRow) => boolean) {
  gantt.value?.filter(cb);
}

defineExpose({
  selectAll,
  unselectAll,
  filter,
});
</script>

<template>
  <div
    id="gantt_here"
    class="w-full h-full"
  />
</template>
